"use client";

import { useEffect, useLayoutEffect, useRef } from "react";
import { toast } from "sonner";
import { create } from "zustand";

export class EditorLeaveCancelledError extends Error {
  readonly name = "EditorLeaveCancelledError";
}

export function isEditorLeaveCancelled(error: unknown) {
  return error instanceof EditorLeaveCancelledError;
}

type LeaveResolver = (allowed: boolean) => void;
type EditorDraft = { dirty: boolean; save?: () => Promise<boolean> };
/** How the open experience saves before leaving; resolves true once saved. */
type LeaveSave = { label: string; run: () => Promise<boolean> };

let pendingLeave: Promise<boolean> | null = null;
let activeTurn: { key: string; stop: () => void } | null = null;
let leaveResolver: LeaveResolver | null = null;

type EditorLeaveState = {
  turnBusy: boolean;
  documentDirty: boolean;
  drafts: Map<symbol, EditorDraft>;
  dirty: boolean;
  /** Set after the user confirms leave, so reload/navigation isn't blocked again. */
  bypass: boolean;
  open: boolean;
  /** Names what has unsaved changes, e.g. the page "Launch". */
  experience: { kind: string; name: string; save?: LeaveSave } | null;
  setDirty: (dirty: boolean) => void;
  confirmLeave: () => Promise<boolean>;
  answerLeave: (allowed: boolean) => void;
};

export const useEditorLeaveStore = create<EditorLeaveState>((set, get) => ({
  turnBusy: false,
  documentDirty: false,
  drafts: new Map(),
  dirty: false,
  bypass: false,
  open: false,
  experience: null,
  setDirty: (documentDirty) => {
    const dirty =
      documentDirty || [...get().drafts.values()].some((draft) => draft.dirty);
    set({ documentDirty, dirty, ...(!dirty && { bypass: false }) });
  },
  confirmLeave: () => {
    if (!canChangeEditorThread()) return Promise.resolve(false);
    if (pendingLeave) return pendingLeave;
    const { dirty, bypass } = get();
    if (!dirty || bypass) return Promise.resolve(true);

    set({ open: true });
    pendingLeave = new Promise<boolean>((resolve) => {
      leaveResolver = resolve;
    });
    return pendingLeave;
  },
  answerLeave: (allowed) => {
    if (!leaveResolver) return;

    set(allowed ? { bypass: true, open: false } : { open: false });
    leaveResolver(allowed);
    leaveResolver = null;
    pendingLeave = null;
  },
}));

/** Includes inline settings drafts in the same Discard/Keep editing decision. */
export function useRegisterEditorDraft(
  dirty: boolean,
  save?: () => Promise<boolean>,
) {
  const saveDraft = useRef(save);
  saveDraft.current = save;
  useLayoutEffect(() => {
    const key = Symbol("editor-draft");
    const update = (draft?: EditorDraft) => {
      const state = useEditorLeaveStore.getState();
      const drafts = new Map(state.drafts);
      if (draft) drafts.set(key, draft);
      else drafts.delete(key);
      const hasChanges =
        state.documentDirty || [...drafts.values()].some((item) => item.dirty);
      useEditorLeaveStore.setState({
        drafts,
        dirty: hasChanges,
        ...(!hasChanges && { bypass: false }),
      });
    };
    update({
      dirty,
      save: () => saveDraft.current?.() ?? Promise.resolve(false),
    });
    return () => update();
  }, [dirty]);
}

/** Flush independently edited settings before saving the page they belong to. */
export async function saveEditorDrafts() {
  const drafts = [...useEditorLeaveStore.getState().drafts.values()].filter(
    (draft) => draft.dirty,
  );
  for (const draft of drafts) {
    if (!(await draft.save?.())) return false;
  }
  return true;
}

/**
 * Names the open experience in the unsaved changes dialog and, while `save`
 * is available, lets the dialog save it and continue.
 */
export function useRegisterLeavingExperience(
  kind: string,
  name: string,
  save?: LeaveSave & { available: boolean },
) {
  // The latest save runs, without re-registering on every render.
  const runSave = useRef(save?.run);
  runSave.current = save?.run;
  const saveLabel = save?.available ? save.label : undefined;
  useEffect(() => {
    const experience = {
      kind,
      name,
      ...(saveLabel && {
        save: {
          label: saveLabel,
          run: () => runSave.current?.() ?? Promise.resolve(false),
        },
      }),
    };
    useEditorLeaveStore.setState({ experience });
    return () => {
      if (useEditorLeaveStore.getState().experience === experience)
        useEditorLeaveStore.setState({ experience: null });
    };
  }, [kind, name, saveLabel]);
}

/** For non-React call sites (logout, shop switch, navigation guard). */
export const confirmLeaveIfNeeded = () =>
  useEditorLeaveStore.getState().confirmLeave();

export const getEditorHasUnsavedChanges = () =>
  useEditorLeaveStore.getState().dirty;

export const isEditorUnloadAllowed = () =>
  useEditorLeaveStore.getState().bypass;

/**
 * Hard-navigate after leave confirmation. Use for routes that live outside the
 * merchant shell (e.g. /admin), where client-side routing keeps ShopProvider mounted.
 */
export async function hardNavigateIfAllowed(url: string) {
  if (!(await confirmLeaveIfNeeded())) return;
  window.location.assign(url);
}

export function canChangeEditorThread() {
  if (!activeTurn) return true;
  const turn = activeTurn;
  toast.info(
    "Wait for the AI to finish, or stop it before switching experiences.",
    {
      id: "editor-active-turn",
      action: { label: "Stop", onClick: () => turn.stop() },
    },
  );
  return false;
}

export function useEditorTurnGuard(
  key: string,
  busy: boolean,
  stop: () => void,
) {
  const latestStop = useRef(stop);
  latestStop.current = stop;
  useLayoutEffect(() => {
    if (!busy) return;
    const turn = { key, stop: () => latestStop.current() };
    activeTurn = turn;
    useEditorLeaveStore.setState({ turnBusy: true });
    return () => {
      if (activeTurn !== turn) return;
      activeTurn = null;
      useEditorLeaveStore.setState({ turnBusy: false });
    };
  }, [key, busy]);
}
